/**
 * Backend 401 `detail` strings, normalized and mapped to user-facing copy.
 *
 * vingapi (`src/auth/dependencies.py`) answers an unauthenticated request with
 * a small, fixed set of `detail` strings:
 *
 *   - "Authentication credentials were not provided."
 *   - "Invalid authentication header format. Use: Token <key>"
 *   - "Invalid token."
 *   - "Token expired."
 *   - "User account is disabled."
 *
 * Those literals are a cross-repo contract (invariant W11) — vingapi pins them
 * in a test that names this module. They are NEVER rendered: they are server
 * vocabulary, not product copy. Everything here converts a detail into either a
 * bounce *reason* (the `?reason=` on `/login/signIn`) or a mapped message from
 * `alertMessages.auth`.
 *
 * Comparison is normalized — trimmed, lowercased, trailing period stripped — so
 * a punctuation or casing drift on the server degrades to the generic branch
 * instead of leaking a raw string into the UI.
 */
import axios from 'axios';
import { alertMessages } from '@/constants';

/** The bounce reason carried on `/login/signIn?reason=…`. */
export type BounceReason = 'session_expired' | 'session_rejected';

/**
 * The server's `detail` for a failed request, when there is one.
 *
 * The interceptor rejects a 401 *before* the 4xx `detail → error.message`
 * rewrite, so `err.message` is the useless "Request failed with status code
 * 401". Callers must read the body instead — that is what this does.
 */
export function readDetail(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = err.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  return typeof detail === 'string' ? detail : undefined;
}

/**
 * Read the `error` code from a structured `detail` envelope:
 * `{detail: {error: 'some_code', message: '...'}}`.
 *
 * vingapi#372 (S3: device binding) sends `token_device_mismatch` this way;
 * the auth-link exchange sends `invalid_link`. The plain-string `readDetail`
 * returns undefined for these; callers that need the code use this.
 */
export function readDetailError(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = err.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const errorCode = (detail as Record<string, unknown>).error;
    return typeof errorCode === 'string' ? errorCode : undefined;
  }
  return undefined;
}

/** Trim, lowercase, strip a single trailing period. */
export function normalizeDetail(s: string | undefined): string {
  if (typeof s !== 'string') return '';
  return s.trim().toLowerCase().replace(/\.$/, '');
}

const EXPIRED = normalizeDetail('Token expired.');
const NO_CREDENTIALS = normalizeDetail('Authentication credentials were not provided.');
const INVALID_TOKEN = normalizeDetail('Invalid token.');
const DISABLED = normalizeDetail('User account is disabled.');

/**
 * Why a 401 bounced the person to the login page.
 *
 * "Token expired." and "Authentication credentials were not provided." are the
 * ordinary end of a session — expected, explained, never reported. Anything
 * else (an invalid token, a disabled account, a header the server could not
 * parse, an unrecognised detail, no detail at all) is a rejection worth a
 * client report. Invariant W7.
 *
 * vingapi#372 (S3): `token_device_mismatch` means the token was minted on a
 * different device — the session is dead for THIS browser. Treated as an
 * ordinary end-of-session (no alert), mapped to the quiet session-expired
 * bounce. The error code arrives in the structured detail envelope
 * `{detail: {error: 'token_device_mismatch'}}`, so the caller must pass the
 * error code extracted by `readDetailError` when the plain string detail is
 * undefined.
 */
export function classifyBounceReason(
  detail: string | undefined,
  errorCode?: string | undefined,
): BounceReason {
  if (errorCode === 'token_device_mismatch') return 'session_expired';
  const normalized = normalizeDetail(detail);
  if (normalized === EXPIRED || normalized === NO_CREDENTIALS) return 'session_expired';
  return 'session_rejected';
}

/**
 * Copy for a failed explicit token exchange (`/sso`, sign-in after
 * password). The raw detail is never rendered.
 *
 *   - "Token expired." / "Invalid token." → the link is dead; offer a fresh one.
 *   - "User account is disabled."         → a seat problem; a new link would
 *                                           send the person round the same loop.
 *   - anything else, including a network error with no response at all
 *                                         → the generic exchange failure.
 */
export function exchangeFailureCopy(detail: string | undefined): string {
  const normalized = normalizeDetail(detail);
  if (normalized === EXPIRED || normalized === INVALID_TOKEN) {
    return alertMessages.auth.linkFailure;
  }
  if (normalized === DISABLED) {
    return alertMessages.auth.accountDeactivated;
  }
  return alertMessages.auth.tokenExchangeFailed;
}

/**
 * The Error object an auth-funnel client report carries.
 *
 * `reportClientError` derives the alert's name and message from the error, so
 * the stage and the server detail must live on the error itself for ops to
 * group and read them. `name` is the alert title; the message is
 * `"<stage>: <detail>"`.
 */
export function authFailureError(stage: string, detail: string): Error {
  const e = new Error(`${stage}: ${detail}`);
  e.name = 'AuthFailure';
  return e;
}
